import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import {
  DataGrid,
} from "@mui/x-data-grid";
import TenantService from "../../services/tenantService";
import StatsService from "../../services/doctorServices/StatsService";
import DoctorPerformanceChartPanel from "../../components/Stats/DoctorPerformanceChartPanel";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import TenantLayout from "../../layouts/TenantLayout";
import CollapsibleListFilters, {
  ListFiltersExcelButton,
  listFiltersSelectProps,
} from "../../components/CollapsibleListFilters";

export default function TenantDoctors() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation(["tenantDashboard", "stats", "team"]);
  const tenantService = useMemo(() => new TenantService(), []);
  const statsService = useMemo(() => new StatsService(), []);
  const navigate = useNavigate();
  const token = localStorage.getItem("tokenTenant");
  const [tableDoctors, setTableDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    doctorName: "",
    email: "",
    role: "",
    specialization: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [allDoctorsStats, setAllDoctorsStats] = useState([]);

  const CustomAccessCell = ({ value }) => (
    <Chip
      label={value === "admin" ? t('roleAdmin') : value === "doctor" ? t('roleDoctor') : value}
      icon={
        value === "admin" ? (
          <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 18 }} />
        ) : value === "doctor" ? (
          <LockOpenOutlinedIcon sx={{ fontSize: 18 }} />
        ) : (
          <SecurityOutlinedIcon sx={{ fontSize: 18 }} />
        )
      }
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: 2,
        fontWeight: 600,
        textTransform: "capitalize",
        background:
          value === "admin"
            ? `linear-gradient(135deg, ${colors.blueAccent[600]}55, ${colors.blueAccent[500]}aa)`
            : value === "doctor"
            ? `linear-gradient(135deg, ${colors.greenAccent[500]}55, ${colors.greenAccent[500]}aa)`
            : `linear-gradient(135deg, ${colors.primary[400]}55, ${colors.primary[500]}aa)`,
        color: colors.grey[100],
        boxShadow: `0 6px 16px rgba(15,23,42,0.18)`,
        '& .MuiChip-icon': {
          color: colors.grey[100],
          ml: 0.2,
        },
      }}
    />
  );

  const columns = useMemo(() => [
    //{ field: "id", headerName: "ID" },
    {
      field: "name",
      headerName: t('columnName'),
      cellClassName: "name-column--cell",
      flex: 0.6,
    }, //name'lere özel sınıf tanımladım bu sayede onu özelleştirebileceğim.
    {
      field: "surname",
      headerName: t('columnSurname'),
      cellClassName: "name-column--cell",
      flex: 0.6,
    }, //name'lere özel sınıf tanımladım bu sayede onu özelleştirebileceğim.

    {
      field: "specialization",
      headerName: t('columnSpecialization'),
      headerAlign: "left", // Sütun başlığının sola hizalanması
      align: "left", // Hücre içeriğinin sola hizalanması
      flex: 0.7,
    },
    { field: "phone_number", headerName: t('columnPhoneNumber'), flex: 0.7 },
    { field: "email", headerName: t('columnEmail'), flex: 0.7 },
    {
      field: "role",
      headerName: t('columnAccessLevel'),
      flex: 1,
      renderCell: (params) => <CustomAccessCell value={params.value} />,
    },
    {
      field: "actions",
      headerName: t("team:actions"),
      width: 88,
      minWidth: 88,
      sortable: false,
      filterable: false,
      disableExport: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title={t("team:viewProfile")}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/tenant/doctors/${params.id}`);
            }}
            sx={{
              color: colors.blueAccent[400],
              "&:hover": {
                backgroundColor: `${colors.blueAccent[700]}33`,
              },
            }}
            aria-label={t("team:viewProfile")}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [t, colors, navigate]);

  const handleRowClick = (params) => {
    navigate(`/tenant/doctors/${params.id}`);
  }; 

  const buildCleanFilters = useCallback((source) => {
    const next = {};
    Object.entries(source).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        next[key] = String(value).trim();
      }
    });
    return next;
  }, []);

  const fetchDoctorsTable = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tenantService.getTenantDoctorsList(token, {
        withMeta: true,
        params: appliedFilters,
        headers: {
          "x-bone-pageindex": pageIndex,
          "x-bone-limit": pageSize,
          "x-bone-sortby": sortBy,
          "x-bone-sortorder": sortOrder,
        },
      });
      setTableDoctors(Array.isArray(response?.data) ? response.data : []);
      setTotalCount(Number(response?.headers?.["x-bone-totalcount"]) || 0);
      setTotalPages(Math.max(Number(response?.headers?.["x-bone-totalpage"]) || 1, 1));
    } catch (error) {
      setTableDoctors([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [tenantService, token, appliedFilters, pageIndex, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    const fetchAllDoctors = async () => {
      try {
        const response = await tenantService.getAllDoctor(token);
        const admins = response?.doctors?.admins ?? [];
        const formattedDoctors =
          admins
            .flatMap((admin) => admin.normalDoctors || [])
            .filter((doctor) => doctor.role === "doctor") ?? [];
        setAllDoctorsStats([...admins, ...formattedDoctors]);
      } catch (error) {
        console.log("Doctorları şekerken bir sorun oluştu.");
      }
    };
    fetchAllDoctors();
  }, [tenantService, token]);

  useEffect(() => {
    fetchDoctorsTable();
  }, [fetchDoctorsTable]);

  const processedRows = useMemo(() => {
    return tableDoctors.map((doctor) => ({
      id: doctor.id,
      name: doctor.name || doctor.firstName || "—",
      surname: doctor.surname || doctor.lastName || "—",
      specialization: doctor.specialization || "—",
      phone_number: doctor.phone_number || doctor.primaryPhone || "—",
      email: doctor.email || "—",
      role: doctor.role || "doctor",
    }));
  }, [tableDoctors]);

  const doctorChartOptions = useMemo(() => {
    return allDoctorsStats.map((row) => ({
      id: row.id,
      label: `${row.name || ""} ${row.surname || ""}`.trim() || row.id,
    }));
  }, [allDoctorsStats]);

  const fetchTenantStats = useCallback((params) => statsService.getTenantStats(params), [statsService]);

  const summaryCards = useMemo(() => [
    {
      label: t('doctorsTotal'),
      value: allDoctorsStats.length,
      sublabel: t('doctorsTotalSublabel'),
      icon: <SecurityOutlinedIcon sx={{ fontSize: 28 }} />,
      gradient: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
    },
    {
      label: t('adminDoctors'),
      value: allDoctorsStats.filter((item) => item.role === "admin").length,
      sublabel: t('doctorsAdministrativeRoles'),
      icon: <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 28 }} />,
      gradient: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[900]} 100%)`,
    },
    {
      label: t('doctorsStandard'),
      value: allDoctorsStats.filter((item) => item.role === "doctor").length,
      sublabel: t('doctorsStandardSublabel'),
      icon: <LockOpenOutlinedIcon sx={{ fontSize: 28 }} />,
      gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
    },
  ], [allDoctorsStats, colors, t]);

  const activeFilterCount = useMemo(
    () => Object.keys(appliedFilters).length,
    [appliedFilters]
  );

  const handleApplyFilters = () => {
    setPageIndex(0);
    setAppliedFilters(buildCleanFilters(filters));
  };

  const handleClearFilters = () => {
    setFilters({
      doctorName: "",
      email: "",
      role: "",
      specialization: "",
      dateFrom: "",
      dateTo: "",
    });
    setPageIndex(0);
    setAppliedFilters({});
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      const response = await tenantService.exportTenantDoctorsExcel(token, appliedFilters);
      const blob = new Blob(
        [response.data],
        { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers?.["content-disposition"] || "";
      const fileNameMatch = disposition.match(/filename="(.+)"/);
      link.href = url;
      link.download = fileNameMatch?.[1] || "tenant-doctors-export.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log("Excel export failed.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <TenantLayout
      contentSx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        backgroundColor: "#f5f7fb",
      }}
    >
      <Grid container spacing={3} sx={{ width: "100%", margin: "0 auto", maxWidth: "1200px" }}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.label}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 4,
                background: card.gradient,
                color: colors.grey[100],
                boxShadow: `0 20px 40px rgba(15, 23, 42, 0.25)`,
              }}
            >
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      backgroundColor: "rgba(255,255,255,0.25)",
                      color: "white",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    {card.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {card.label}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      {card.sublabel}
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="h3" sx={{ fontWeight: 800 }}>
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          width: "100%",
          maxWidth: "1200px",
          margin: "0 auto",
          backgroundColor: colors.primary[500],
          borderRadius: 4,
          boxShadow: `0 24px 48px rgba(15, 23, 42, 0.35)`,
          border: `1px solid ${colors.primary[300]}55`,
          p: { xs: 2, md: 3 },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            mb: 2,
            fontWeight: 700,
            color: colors.grey[100],
            letterSpacing: 0.4,
          }}
        >
          {t('doctorsDirectory')}
        </Typography>

        <CollapsibleListFilters
          colors={colors}
          open={filtersOpen}
          onToggle={() => setFiltersOpen((prev) => !prev)}
          activeFilterCount={activeFilterCount}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          exportAction={
            <ListFiltersExcelButton
              colors={colors}
              onClick={handleExportExcel}
              disabled={isExporting}
              loading={isExporting}
            />
          }
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2 }}>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                fullWidth
                size="small"
                label="Doctor Name"
                value={filters.doctorName}
                onChange={(e) => setFilters((prev) => ({ ...prev, doctorName: e.target.value }))}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                value={filters.email}
                onChange={(e) => setFilters((prev) => ({ ...prev, email: e.target.value }))}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Role"
                value={filters.role}
                onChange={(e) => setFilters((prev) => ({ ...prev, role: e.target.value }))}
                SelectProps={listFiltersSelectProps(colors)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="doctor">Doctor</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                fullWidth
                size="small"
                label="Specialization"
                value={filters.specialization}
                onChange={(e) => setFilters((prev) => ({ ...prev, specialization: e.target.value }))}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date From"
                value={filters.dateFrom}
                onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date To"
                value={filters.dateTo}
                onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </CollapsibleListFilters>

        <DataGrid
          rows={processedRows}
          columns={columns}
          disableColumnMenu={false}
          disableRowSelectionOnClick
          loading={loading}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          paginationModel={{ page: pageIndex, pageSize }}
          onPaginationModelChange={(model) => {
            if (model.page !== pageIndex) setPageIndex(model.page);
            if (model.pageSize !== pageSize) {
              setPageIndex(0);
              setPageSize(model.pageSize);
            }
          }}
          pageSizeOptions={[10, 25, 50]}
          sx={{
            border: "none",
            color: colors.grey[100],
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: `${colors.blueAccent[700]}80`,
              borderBottom: "none",
              fontWeight: 700,
              letterSpacing: 0.4,
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[500],
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: `${colors.blueAccent[700]}33`,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${colors.primary[400]}55`,
            },
            "& .MuiTablePagination-root": {
              color: colors.grey[200],
            },
          }}
          onRowClick={handleRowClick}
        />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ mt: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`Total: ${totalCount}`} size="small" />
            <Chip label={`Page: ${Math.min(pageIndex + 1, totalPages)} / ${totalPages}`} size="small" />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              select
              size="small"
              label="Sort By"
              value={sortBy}
              onChange={(e) => {
                setPageIndex(0);
                setSortBy(e.target.value);
              }}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="createdAt">Created</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="surname">Surname</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Direction"
              value={sortOrder}
              onChange={(e) => {
                setPageIndex(0);
                setSortOrder(e.target.value);
              }}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="DESC">DESC</MenuItem>
              <MenuItem value="ASC">ASC</MenuItem>
            </TextField>
          </Stack>
          <Pagination
            color="primary"
            shape="rounded"
            page={Math.min(pageIndex + 1, totalPages)}
            count={totalPages}
            onChange={(_, value) => setPageIndex(value - 1)}
          />
        </Stack>
      </Box>

      <DoctorPerformanceChartPanel doctorOptions={doctorChartOptions} fetchStats={fetchTenantStats} />
    </TenantLayout>
  );
}
